import { frameCopy } from '@/copy'
import { resourcesCopy } from '@/copy/resources'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { CodeBlock } from '@/ui/CodeBlock/CodeBlock'
import { CheckboxField, SelectField, TextField } from '@/ui/Field/Field'
import { Icon } from '@/ui/Icon/Icon'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { FieldGroup } from './FieldGroup'
import {
  BLANK_RULE,
  type EntityFact,
  type RuleDraft,
  ruleBody,
} from './normalizeRules'
import { PredicateTermRows } from './PredicateTermRows'

interface Props {
  rules: RuleDraft[]
  onRules: (rules: RuleDraft[]) => void
}

/**
 * What each incoming request means: the first rule whose conditions match
 * states its facts — event type, which item it is about (requests about
 * one item share a thread), what happened to that item — or drops it.
 */
export function NormalizeRulesEditor({ rules, onRules }: Props) {
  const copy = resourcesCopy.forms.normalize
  const update = (i: number, patch: Partial<RuleDraft>) =>
    onRules(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const remove = (i: number) => onRules(rules.filter((_, j) => j !== i))

  return (
    <FieldGroup label={copy.rules} help={copy.help}>
      {rules.map((rule, i) => {
        const n = i + 1
        return rule.kept ? (
          <CodeBlock
            // biome-ignore lint/suspicious/noArrayIndexKey: order is the identity
            key={i}
            // "Rule n" is a word, not an id: the sans caption (CodeBlock card).
            caption={[
              copy.rule(n),
              typeof rule.kept.profile === 'string'
                ? copy.managedBy(rule.kept.profile)
                : copy.keptAsStored,
            ].join(frameCopy.codeBlock.captionJoiner)}
            wrap
          >
            {JSON.stringify(ruleBody(rule), null, 2)}
          </CodeBlock>
        ) : (
          <Card
            // biome-ignore lint/suspicious/noArrayIndexKey: order is the identity
            key={i}
            header={
              <>
                <Text weight="strong">{copy.rule(n)}</Text>
                <Button
                  variant="ghost"
                  ground="surface"
                  aria-label={copy.removeRule(n)}
                  onClick={() => remove(i)}
                >
                  {copy.remove}
                </Button>
              </>
            }
          >
            <Stack gap="space-12">
              <PredicateTermRows
                label={copy.conditions}
                help={copy.conditionsHelp}
                rows={rule.conditions}
                onRows={(conditions) => update(i, { conditions })}
                ground="surface"
              />
              <CheckboxField
                id={`rule-${n}-drop`}
                ground="surface"
                label={copy.drop}
                checked={rule.drop}
                onChecked={(drop) => update(i, { drop })}
              />
              {!rule.drop && (
                <>
                  <TextField
                    id={`rule-${n}-type`}
                    label={copy.eventType}
                    value={rule.event_type}
                    onValue={(event_type) => update(i, { event_type })}
                    inputProps={{ placeholder: copy.eventTypePlaceholder }}
                  />
                  <TextField
                    id={`rule-${n}-key`}
                    label={copy.item}
                    help={copy.itemHelp}
                    value={rule.correlation_key}
                    onValue={(correlation_key) =>
                      update(i, { correlation_key })
                    }
                    inputProps={{ placeholder: copy.itemPlaceholder }}
                  />
                  <SelectField
                    id={`rule-${n}-entity`}
                    label={copy.entity}
                    value={rule.entity}
                    onValue={(v) => update(i, { entity: v as EntityFact })}
                  >
                    <option value="">{copy.entities.none}</option>
                    <option value="closed">{copy.entities.closed}</option>
                    <option value="updated">{copy.entities.updated}</option>
                    <option value="reopened">{copy.entities.reopened}</option>
                  </SelectField>
                  <TextField
                    id={`rule-${n}-message`}
                    label={copy.message}
                    hint={resourcesCopy.form.optional}
                    value={rule.agent_message}
                    onValue={(agent_message) => update(i, { agent_message })}
                    inputProps={{ placeholder: copy.messagePlaceholder }}
                  />
                  <TextField
                    id={`rule-${n}-dedup`}
                    label={copy.dedup}
                    hint={resourcesCopy.form.optional}
                    help={copy.dedupHelp}
                    value={rule.dedup_key}
                    onValue={(dedup_key) => update(i, { dedup_key })}
                    inputProps={{ placeholder: copy.dedupPlaceholder }}
                  />
                </>
              )}
            </Stack>
          </Card>
        )
      })}
      <Inline gap="space-8">
        <Button variant="ghost" onClick={() => onRules([...rules, BLANK_RULE])}>
          <Icon name="plus" size="icon-embedded" />
          {copy.add}
        </Button>
      </Inline>
    </FieldGroup>
  )
}
